// src/composables/useEvalForm.js
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from '@/composables/use-toast';
import { fetchDatasetById, updateDataset, uploadDatasetFile, revertDatasetVersion } from '@/services/api';
import { processEvalBuilderSubmission, uploadEvalFile } from '@/services/evalService';
import { prepareEvalBuilderFiles } from '@/pages/dataset/services/eval/evalBuilderFileService';
import { mapDatasetToState } from '@/pages/eval/eval-builder/services/evalMapper';
import { getFileMetadata, validateFileExtension } from '@/pages/eval/eval-builder/utils/fileUtils';

export function useEvalForm() {
  const router = useRouter();
  const { addToast } = useToast();

  // State
  const evalName = ref('');
  const selectedOwner = ref('');
  const originalOwner = ref('');
  const originalOwnerName = ref('');
  const tasks = ref([]);
  const activeTaskId = ref(null);
  const versionsOptions = ref([]);
  const selectedVersionId = ref(null);
  const lastVersion = ref(null);
  
  // File State
  const coreDatasetFile = ref(null);
  const existingCoreDatasetInfo = ref(null);
  const uploadProgress = ref(0);
  const coreDatasetFileError = ref('');

  // Control State
  const isSaving = ref(false);
  const isLoadingDataset = ref(false);
  const editingDatasetId = ref(null);
  const savedSuccessfully = ref(false);
  const createdDatasetId = ref(null);
  const editingEvalId = ref(null);

  // Helpers
  
  const createEmptyTask = () => ({
    id: crypto.randomUUID(),
    prompt: "",
    answer: "",
    references: [],
    tools: [],
    criteria: [],
    metadata: {
      difficulty: 'medium',
      category: '',
      tags: [],
      environment_profile: null,
      expert_time_estimate_min: null,
      junior_time_estimate_min: null,
    },
    environment: {
      base_image: 'ubuntu:24.04',
      system_packages: [],
      python_packages: [],
      dockerfile: '',
      resources: {
        cpus: 1,
        memory_mb: 2048,
        storage_mb: 10240,
        gpus: 0,
      },
      timeouts: {
        agent_sec: 900,
        verifier_sec: 900,
        build_sec: 600,
      },
    },
    passThreshold: 70
  });

  const initializeForm = () => {
    evalName.value = '';
    selectedOwner.value = '';
    tasks.value = [createEmptyTask()];
    activeTaskId.value = tasks.value[0].id;
    coreDatasetFile.value = null;
    existingCoreDatasetInfo.value = null;
    editingDatasetId.value = null;
    savedSuccessfully.value = false;
    editingEvalId.value = null;
    versionsOptions.value = [];
    selectedVersionId.value = null;
    lastVersion.value = null;
  };

  // File Logic

  const handleFileSelect = (file) => {
    if (!validateFileExtension(file)) {
      coreDatasetFileError.value = 'Invalid file extension.';
      return;
    }
    coreDatasetFileError.value = '';
    coreDatasetFile.value = file;
  };

  const uploadCoreDataset = async (ownerId) => {
    if (!coreDatasetFile.value) return true;
    try {
      const res = await uploadDatasetFile(coreDatasetFile.value, ownerId, (p) => uploadProgress.value = p);
      return res === 200 || res === 201;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  // Load Logic (Edit)

  const resolveVersionList = (dataset) => {
    if (Array.isArray(dataset?.versions?.available)) {
      return dataset.versions.available;
    }
    if (Array.isArray(dataset?.versions)) {
      return dataset.versions;
    }
    return [];
  };

  const applyVersionSelection = (dataset, requestedVersion) => {
    const versionList = resolveVersionList(dataset);
    versionsOptions.value = versionList.map(version => ({
      value: Number(version.version),
      label: `v${version.version}`
    }));

    if (versionsOptions.value.length === 0) {
      lastVersion.value = null;
      selectedVersionId.value = null;
      return;
    }

    const lastValue = dataset?.versions?.last
      ? Number(dataset.versions.last)
      : Math.max(...versionsOptions.value.map(option => option.value));

    lastVersion.value = Number.isFinite(lastValue) ? lastValue : versionsOptions.value[0].value;
    const availableVersions = new Set(versionsOptions.value.map(option => option.value));
    if (requestedVersion && availableVersions.has(requestedVersion)) {
      selectedVersionId.value = requestedVersion;
      return;
    }
    selectedVersionId.value = dataset?.versions?.current
      ? Number(dataset.versions.current)
      : lastVersion.value;
  };

  const applyDatasetState = (dataset, requestedVersion) => {
    editingDatasetId.value = dataset.id;
    editingEvalId.value = dataset.eval_id || null;
    evalName.value = dataset.name;
    selectedOwner.value = dataset.seller?.user?.id;
    originalOwner.value = dataset.seller?.user?.id || '';
    originalOwnerName.value = dataset.seller?.user?.username || '';

    if (dataset.file_name) {
      existingCoreDatasetInfo.value = {
        name: dataset.file_name,
        size: dataset.file_size,
        extension: dataset.file_extension
      };
    }

    const mappedTasks = mapDatasetToState(dataset);
    tasks.value = mappedTasks;
    activeTaskId.value = mappedTasks.length > 0 ? mappedTasks[0].id : null;

    applyVersionSelection(dataset, requestedVersion);
  };

  const loadDataset = async (id, versionId) => {
    if (!id) return;
    isLoadingDataset.value = true;
    try {
      const dataset = await fetchDatasetById(id, versionId, true);
      if (!dataset) throw new Error('Dataset not found');
      const requestedVersion = Number.isFinite(Number(versionId)) ? Number(versionId) : null;

      applyDatasetState({ ...dataset, id }, requestedVersion);
    } catch (error) {
      addToast('Error loading eval', 'error');
      console.error(error);
    } finally {
      isLoadingDataset.value = false;
    }
  };

  // Save Logic (Create)

  const createEval = async (isAdmin, createSocketFn, evalType = 'qa') => {
    if (!selectedOwner.value) return addToast('Select an owner', 'error');
    
    isSaving.value = true;
    try {
      // 1. Upload Core Dataset (Optional)
      if (!(await uploadCoreDataset(selectedOwner.value))) throw new Error('Core dataset upload failed');

      const fileMeta = coreDatasetFile.value ? getFileMetadata(coreDatasetFile.value) : null;
      
      // 2. Process Submission
      const result = await processEvalBuilderSubmission({
        evalName: evalName.value,
        selectedOwner: selectedOwner.value,
        isAdmin,
        tasks: tasks.value,
        evalType,
        createEvalWithWebSocket: createSocketFn,
        coreDatasetInfo: fileMeta ? {
            file_name: fileMeta.fullFileName,
            file_size: fileMeta.size,
            file_extension: fileMeta.fileExtension
        } : null,
        onProgress: (p) => uploadProgress.value = p
      });

      if (!result.success) throw new Error(result.error);
      
      savedSuccessfully.value = true;
      createdDatasetId.value = result.data?.id;
      return true; // Success signal

    } catch (error) {
    addToast(error.message, 'error');
      return false;
    } finally {
      isSaving.value = false;
    }
  };

  // Update Logic (Edit)

  const updateEval = async (evalType = 'qa') => {
    if (!editingDatasetId.value) return;
    if (!evalName.value?.trim()) {
      addToast('Eval name is required', 'error');
      return;
    }
    
    isSaving.value = true;
    try {
      // 1. Prepare files
      const baseVersion = Number(lastVersion.value ?? selectedVersionId.value ?? 0);
      const nextVersion = Number.isFinite(baseVersion) ? baseVersion + 1 : 1;
      const preparation = await prepareEvalBuilderFiles(tasks.value, nextVersion, evalType);
      if (preparation.errors?.length) throw new Error(preparation.errors[0]);

      // 2. Upload JSONL Eval chunks
      const evalId = await uploadEvalFile(
        preparation.files,
        selectedOwner.value,
        p => uploadProgress.value = p,
        editingEvalId.value,
        evalType
      );
      if (!evalId) throw new Error('Failed to upload eval files');

      // 3. Upload Core Dataset if changed
      if (!(await uploadCoreDataset(selectedOwner.value))) throw new Error('Core dataset upload failed');

      // 4. Metadata Payload
      const meta = coreDatasetFile.value ? getFileMetadata(coreDatasetFile.value) : {
        file_name: existingCoreDatasetInfo.value?.name || "",
        file_size: existingCoreDatasetInfo.value?.size || 0,
        file_extension: existingCoreDatasetInfo.value?.extension || ""
      };

      const payload = {
        dataset: {
          file_name: meta.fullFileName || meta.file_name,
          file_size: meta.size || meta.file_size,
          file_extension: meta.fileExtension || meta.file_extension,
          name: evalName.value,
          description: 'Updated via Eval Builder',
          owner_user_id: selectedOwner.value,
          eval_id: evalId,
        },
        comment: 'Dataset updated via Eval Builder'
      };

      const res = await updateDataset(payload, editingDatasetId.value);
      if (res?.status !== 200 && res?.status !== 201) throw new Error('API update failed');
      
      savedSuccessfully.value = true;
      
      router.push('/creator-profile?page=eval-datasets&subpage=eval-datasets');

    } catch (error) {
      console.error(error.message)
    } finally {
      isSaving.value = false;
    }
  };

  const clearVersions = () => {
    versionsOptions.value = [];
    selectedVersionId.value = null;
    lastVersion.value = null;
  };

  const revertVersion = async () => {
    try {
      const response = await revertDatasetVersion(editingDatasetId.value, selectedVersionId.value);
      if (!response) throw new Error('Failed to revert version');
      await loadDataset(editingDatasetId.value);
      addToast('Eval reverted successfully', 'success');
    } catch (error) {
      console.error(error.message)
    }
  }

  return {
    // State
    evalName,
    selectedOwner,
    originalOwner,
    originalOwnerName,
    tasks,
    activeTaskId,
    coreDatasetFile,
    existingCoreDatasetInfo,
    versionsOptions,
    selectedVersionId,
    uploadProgress,
    coreDatasetFileError,
    // Status
    isSaving,
    isLoadingDataset,
    editingDatasetId,
    savedSuccessfully,
    createdDatasetId,
    editingEvalId,
    lastVersion,
    // Actions
    initializeForm,
    handleFileSelect,
    loadDataset,
    createEval,
    updateEval,
    clearVersions,
    revertVersion,
    addTask: () => {
      const t = createEmptyTask();
      tasks.value.push(t);
      activeTaskId.value = t.id;
      return t;
    },
    deleteTask: (id) => {
      const idx = tasks.value.findIndex(t => t.id === id);
      if (idx !== -1) {
        tasks.value.splice(idx, 1);
        if (id === activeTaskId.value) activeTaskId.value = tasks.value[0]?.id ?? null;
      }
    },
    updateTask: (updatedTask) => {
      const idx = tasks.value.findIndex(t => t.id === updatedTask.id);
      if (idx !== -1) {
        tasks.value[idx] = updatedTask;
      }
    }
  };
}
